import { take, forkJoin, filter, Observable, BehaviorSubject, retry } from 'rxjs';
import createEventBlocks from './EventBlocks.mjs';
import eventTypeEnum from './EventTypeEnum.mjs';
import createJobQueue from '../utils/JobQueue.mjs';
import eventAttributes from './EventAttributes.mjs';
import statusEnum from './StatusEnum.mjs';
import feedbackCodeEnum from './FeedbackCodeEnum.mjs';
import feedbackCategoryEnum from './FeedbackCategoryEnum.mjs';
import garageDoorActionEnum from './GarageDoorActionEnum.mjs';
import garageSpotActionEnum from './GarageSpotActionEnum.mjs';
//import garageSpotIdEnum from './GarageSpotIdEnum.mjs';

/**
 * @module EventService
 */
function createEventService(
    apiService,
    deviceService,
    userService,
    logService,
    faceService,
    eventFilter,
    environmentService,
    util,
    _window) {

    const _eventService = {};
    const allEvents = [];
    const activeEvents = [];
    const activeEventsSubject = new BehaviorSubject({ actives: activeEvents, all: allEvents });
    const singleSubjects = {};
    const lookup = {};
    const unfinished = [];
    const checkForNewMinimumInterval = 7000;
    const lastLoadNewInterval = 500;
    const eventRefreshTriesAllowed = 30;
    const subscriptions = [];
    const bulkRemovesSubject = new BehaviorSubject(0);

    let lastLoadTime = _window.Date.now(); // + checkForNewMinimumInterval;
    let lastLoadNewDate;
    let timerForNewEvents;
    let loadMoreInProcess;
    let loadForTimeRangeInProcess;
    let devicesSubscription;
    let loggedThumbUriError = false;

    const eventTagProperties = Object.freeze({
        person: 'peopleCount',
        object: 'objectCount',
        cat: 'catCount',
        dog: 'dogCount',
        car: 'carCount',
        baby: 'babyCount',
        pinUsed: 'rel/PIN_USED',
        invalidPin: 'rel/INVALID_PIN',
        call: 'rel/CALL_BUTTON'
    });

    const eventBlocks = createEventBlocks(logService, util, _window);
    const jobQueue = createJobQueue({ window: _window, concurrentJobs: 2 });

    // =================
    // private methods
    // =================

    function log(params) {
        params.module = 'eventService';
        logService.log(params);
        return params;
    }

    function clear() {
        if (devicesSubscription) {
            devicesSubscription.unsubscribe();
            devicesSubscription = undefined;
        }
        bulkRemovesSubject.next(0);
        subscriptions.forEach(sub => sub.unsubscribe());
        subscriptions.splice(0);
        jobQueue.clear();
        eventBlocks.clear();
        eventFilter.clear();
        allEvents.splice(0);
        activeEvents.splice(0);
        activeEventsSubject.next({ actives: activeEvents, all: allEvents });
        unfinished.splice(0);
        util.clear(lookup);
        lastLoadTime = _window.Date.now(); // + checkForNewMinimumInterval;
        lastLoadNewDate = undefined;
        _window.clearTimeout(timerForNewEvents);
        timerForNewEvents = undefined;
        loadMoreInProcess = undefined;
        loadForTimeRangeInProcess = false;
        util.clear(singleSubjects);
    }

    function byDateValue(o) {
        return o.dateValue === this.dateValue;
    }

    function byEvent(o) {
        return o.event === this.event;
    }

    function byDateValueAndsrcId(event) {
        return event.dateValue === this.dateValue && event.srcId === this.srcId;
    }

    function dateOnly(date) {
        return new _window.Date(date.getFullYear(), date.getMonth(), date.getDate());
    }

    /**
     * @description Converts event tags from raw grg.door to a garageDoors array property.
     * @param {String} key Event tag key
     * @param {Object} tags event.tags
     * @example tags.garageDoors = [ { "doorId": "3", "action": "open", "offset": 7 } ]
     * @example tags.garageDoorOpen = true
     */
    function CalcEventTagsGarageDoor(key, tags) {
        if (key.startsWith('grg.door.')) {
            let garageData = key.substring(9).split('.');
            if (garageData.length === 2) {
                // create tags.garageDoors if not already there
                tags.garageDoors ??= [];
                // push to garageDoors
                tags.garageDoors.push({
                    doorId: garageData[0],
                    action: garageData[1],
                    offset: isNaN(tags[key]) ? 0 : parseFloat(tags[key])
                });
                // shortcut properties for filtering
                if (garageData[1] === 'open') {
                    tags.garageDoorOpen = true;
                } else if (garageData[1] === 'close') {
                    tags.garageDoorClose = true;
                }
                // remove original data that came from the server
                tags[key] = undefined;
                delete tags[key];
            }
        }
    }

    /**
     * @description Converts event tags from raw grg.spot to a garageSpots array property.
     * @param {String} key Event tag key
     * @param {Object} tags event.tags
     * @example tags.garageSpots = [ { "spotId": "right", "action": "exit", "offset": 4 } ]
     * @example tags.garageSpotEnter = true
     */
    function CalcEventTagsGarageSpot(key, tags) {
        if (key.startsWith('grg.spot.')) {
            let garageData = key.substring(9).split('.');
            if (garageData.length === 2) {
                // create tags.garageSpots if not already there
                tags.garageSpots ??= [];
                // push to garageSpots
                tags.garageSpots.push({
                    spotId: garageData[0],
                    action: garageData[1],
                    //vehicleId: garageData[2],
                    offset: isNaN(tags[key]) ? 0 : parseFloat(tags[key])
                });
                // shortcut properties for filtering
                if (garageData[1] === 'enter') {
                    tags.garageSpotEnter = true;
                } else if (garageData[1] === 'exit') {
                    tags.garageSpotExit = true;
                }
                // remove original data that came from the server
                tags[key] = undefined;
                delete tags[key];
            }
        }
    }

    function addAttribute(event, eventAttribute) {
        if (event && eventAttribute) {
            if (!Array.isArray(event.attributes)) {
                event.attributes = [];
            }
            event.attributes.push(eventAttribute);
        }
    }

    function calculateEventTags(event) {
        if (event.tags !== undefined) {
            const tags = event.tags;

            // convert all "count" properties to numbers
            Object.keys(tags).forEach(key => {
                if (key.endsWith('Count') && typeof tags[key] === 'string') {
                    try {
                        tags[key] = parseInt(tags[key], 10);
                    } catch (ignore) {
                        tags[key] = 0;
                    }
                }
            });

            // adjust object count so people and objects don't get counted twice
            if (tags.objectCount !== undefined) {
                tags.objectCountAdjusted = tags.objectCount;
                if (tags.peopleCount !== undefined) {
                    tags.objectCountAdjusted = tags.objectCount - tags.peopleCount;
                    tags.objectCountAdjusted = Math.max(0, tags.objectCountAdjusted);
                }
            }

            // unknown detections should convert to objects
            Object.keys(tags).forEach(key => {
                if (key.endsWith('Count')
                        && typeof tags[key] === 'number'
                        && tags[key] > 0
                        && !Object.values(eventTagProperties).some(value => value === key)) {
                    if (tags.objectCountAdjusted) {
                        tags.objectCountAdjusted += 1;
                    } else {
                        tags.objectCountAdjusted = 1;
                    }
                }
                CalcEventTagsGarageSpot(key, tags);
                CalcEventTagsGarageDoor(key, tags);
            });

        }
    }

    function addValueAttribute(event, eventAttribute) {
        if (event
                && eventAttribute
                && typeof event.tags[eventTagProperties[eventAttribute.id]] === 'number'
                && event.tags[eventTagProperties[eventAttribute.id]] > 0) {
            addAttribute(event, eventAttribute);
        }
    }

    function calcEventIconsPin(tags, event) {
        // These are regular video events that have been associated with a PIN event,
        // so need to add the pin attribute
        if (tags[eventTagProperties.pinUsed]) {
            tags.pinUsed = tags[eventTagProperties.pinUsed];
            delete tags[eventTagProperties.pinUsed];
        }
        if (tags[eventTagProperties.invalidPin]) {
            tags.invalidPin = tags[eventTagProperties.invalidPin];
            delete tags[eventTagProperties.invalidPin];
        }
        if (tags.pinUsed || tags.invalidPin) {
            addAttribute(event, eventAttributes.pin);
        }
    }

    function calcEventIconsCall(tags, event) {
        // These are regular video events that have been associated with a call,
        // so need to add the call attribute
        if (tags[eventTagProperties.call]) {
            tags.call = tags[eventTagProperties.call];
            delete tags[eventTagProperties.call];
            addAttribute(event, eventAttributes.call);
        }
    }

    function calcEventIconsGarageSpots(tags, event) {
        if (Array.isArray(tags.garageSpots) && tags.garageSpots.length > 0) {
            let hasEnter;
            let hasExit;
            tags.garageSpots.forEach(function(garageData) {
                if (garageData.action === garageSpotActionEnum.enter && !hasEnter) {
                    addAttribute(event, eventAttributes.garageEnter);
                    hasEnter = true;
                } else if (garageData.action === garageSpotActionEnum.exit && !hasExit) {
                    addAttribute(event, eventAttributes.garageExit);
                    hasExit = true;
                }
            });
        }
    }

    function calcEventIconsGarageDoors(tags, event) {
        if (Array.isArray(tags.garageDoors) && tags.garageDoors.length > 0) {
            let hasOpen;
            let hasClose;
            tags.garageDoors.forEach(function(garageData) {
                if (garageData.action === garageDoorActionEnum.open && !hasOpen) {
                    addAttribute(event, eventAttributes.garageOpen);
                    hasOpen = true;
                } else if (garageData.action === garageDoorActionEnum.close && !hasClose) {
                    addAttribute(event, eventAttributes.garageClose);
                    hasClose = true;
                }
            });
        }
    }

    function eventAttributesSort(a, b) {
        if (a.sort > b.sort) {
            return 1;
        }
        if (a.sort < b.sort) {
            return -1;
        }
        if (a.id < b.id) {
            return 1;
        }
        if (a.id > b.id) {
            return -1;
        }
        return 0;
    }

    /* eslint-disable */
    function calculateEventAttributes(event) {
        const tags = event.tags;

        if (Array.isArray(event.attributes)) {
            event.attributes.splice(0);
        }

        if (event.favorite) {
            addAttribute(event, eventAttributes.favorite);
        }

        if (event.type === eventTypeEnum.highlight) {
            addAttribute(event, eventAttributes.highlight);
        }

        // pinUsed and invalidPin event types normally will not be shown,
        // but if the asociated video event is not avaiable, then we might show them.
        // Therefore a pin attribute is needed.
        if (event.type === eventTypeEnum.pinUsed
                || event.type === eventTypeEnum.invalidPin) {
            addAttribute(event, eventAttributes.pin);
        }

        // add a call attribute to call events in case the event is ever displayed.
        if (event.type === eventTypeEnum.callButton) {
            addAttribute(event, eventAttributes.call);
        }

        if (tags) {
            addValueAttribute(event, eventAttributes.person);
            addValueAttribute(event, eventAttributes.car);
            addValueAttribute(event, eventAttributes.baby);

            calcEventIconsPin(tags, event);
            calcEventIconsCall(tags, event);

            if (tags.delivery) {
                addAttribute(event, eventAttributes.delivery);
            }

            if (tags.objectCountAdjusted) {
                addAttribute(event, eventAttributes.object);
            }

            if (tags.autoSecureEventId) {
                addAttribute(event, eventAttributes.autoSecure);
            }

            if (tags.animalCount) {
                addAttribute(event, eventAttributes.animal);
            }

            if (tags.movingPackageCount) {
                addAttribute(event, eventAttributes.package);
            }

            if (tags.movingVehicleCount) {
                addAttribute(event, eventAttributes.vehicle);
            }

            if (((tags.catCount && tags.catCount > 0)
                    || (tags.dogCount && tags.dogCount > 0))) {
                addAttribute(event, eventAttributes.pet);
            }

            calcEventIconsGarageSpots(tags, event);
            calcEventIconsGarageDoors(tags, event);
        }

        if (Array.isArray(event.attributes)) {
            event.attributes.sort(eventAttributesSort);
        }
    }
    /* eslint-enable */

    function calculateEventFaces(event) {
        if (event.tags) {
            Object.keys(event.tags).forEach(key => {
                if (key.startsWith('faceId:')) {
                    const objId = key.slice(7);
                    const faceId = event.tags[key];
                    event.faces ??= {};
                    event.faces[faceId] ??= { obj: {} };
                    event.faces[faceId].obj[objId] = {};
                }
            });

            if (event.faces) {
                Object.keys(event.tags).forEach(key => {
                    if (key.startsWith('faceCrop:')) {
                        const cropTag = event.tags[key];
                        const cropNum = key.slice(9);
                        Object.keys(event.faces).forEach(faceKey => {
                            if (event.faces[faceKey].obj[cropTag]) {

                                if (!Array.isArray(event.faces[faceKey].obj[cropTag].crops)) {
                                    event.faces[faceKey].obj[cropTag].crops = [];
                                }

                                event.faces[faceKey].obj[cropTag].crops.push(cropNum);
                            }
                        });
                    }
                    if (key.startsWith('faceId:') || key.startsWith('faceCrop:') || key.startsWith('face:')) {
                        delete event.tags[key];
                    }
                });
            }
        }
    }

    function isUnfinalized (event) {
        return event.status === statusEnum.open
            || event.status === statusEnum.processing
            || event.status === statusEnum.finalizing
            || event.status === statusEnum.prefinalizing;
    }

    /**
     * @param {Object} oldEvent may be an existing event or raw data from the server
     */
    function calculateEventProperties(oldEvent) {

        /**
         * @description There are more simple ways to create a date from a string, but do not do it.
         * See {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Date}
         */
        const date = oldEvent.date || new _window.Date(
            parseInt(oldEvent.dttm.substring(0, 4), 10),
            parseInt(oldEvent.dttm.substring(5, 7), 10) - 1,
            parseInt(oldEvent.dttm.substring(8, 10), 10),
            parseInt(oldEvent.dttm.substring(11, 13), 10),
            parseInt(oldEvent.dttm.substring(14, 16), 10),
            parseInt(oldEvent.dttm.substring(17, 19), 10)
        );

        // This is the one source of all event objects with all properties pre-defined
        // to improve performance
        const event = {
            id: oldEvent.id,
            type: oldEvent.type,
            media: oldEvent.media,
            srcId: oldEvent.srcId,
            srcType: oldEvent.srcType,
            tags: oldEvent.tags || oldEvent.values,
            favorite: oldEvent.favorite,
            status: oldEvent.status, // module:EventService.StatusEnum
            duration: oldEvent.duration,
            viewed: oldEvent.viewed,
            eventLife: oldEvent.eventLife,
            faces: undefined,
            attributes: undefined,
            device: undefined,
            date: date,
            dateValue: dateOnly(date).getTime(),
            fullDateValue: date.getTime(),
            shareCode: oldEvent.shareCode,
            rels: undefined,
            stale: false
        };

        calculateEventFaces(event);
        calculateEventTags(event);
        calculateEventAttributes(event);
        return event;
    }

    // Events may have the same fullDateValue, so make the sort order more deterministic
    // by secondarily sorting on id
    function eventSort(a, b) {
        if (a.fullDateValue !== b.fullDateValue) {
            return a.fullDateValue - b.fullDateValue;
        }
        if (a.id > b.id) {
            return 1;
        }
        if (a.id < b.id) {
            return -1;
        }
        return 0;
    }

    function removeFromUnfinished(event) {
        const item = unfinished.find(byEvent, { event: event });
        if (item) {
            const index = unfinished.indexOf(item);
            if (index > -1) {
                unfinished.splice(index, 1);
            }
        }
    }

    function addActive(event) {
        if (eventFilter.isActive(event)) {
            activeEvents.push(event);
        }
    }

    function setActives() {
        activeEvents.splice(0);
        allEvents.forEach(addActive);
    }

    function removeFromClient(params) {
        const toRemove = lookup[params.id];
        if (toRemove) {
            let removeIndex = allEvents.indexOf(toRemove);
            if (removeIndex > -1) {
                // remove from events
                allEvents.splice(removeIndex, 1);
                delete lookup[params.id];
                // remove from actives
                removeIndex = activeEvents.indexOf(toRemove);
                if (removeIndex > -1) {
                    activeEvents.splice(removeIndex, 1);
                }
                deviceService.refreshHasEvents(params).subscribe(() => {
                    activeEventsSubject.next({ actives: activeEvents, all: allEvents });
                });
            }
        }
    }

    function processEvent(rawEvent) {
        try {
            let existingEvent = lookup[rawEvent.id];

            // handle events that should be removed
            if (rawEvent.status === statusEnum.deleted || rawEvent.status === statusEnum.hidden) {
                removeFromClient({ id: rawEvent.id });
                this.removed.push(rawEvent);
                return;
            }

            if (!existingEvent) {
                // add event
                existingEvent = calculateEventProperties(rawEvent);
                lookup[existingEvent.id] = existingEvent;
                allEvents[this.addMethod](existingEvent);
                this.added.push(existingEvent);
                this.items.push(existingEvent);

                // handle finalized state
                if (isUnfinalized(existingEvent)) {
                    unfinished.push({
                        event: existingEvent,
                        now: this.now,
                        tries: 0
                    });
                }
            } else {
                if (isUnfinalized(existingEvent) && !isUnfinalized(rawEvent)) {
                    // if the event has become finalized, stop trying to load it
                    removeFromUnfinished(existingEvent);
                }

                // update existing
                existingEvent.attributes = undefined;
                existingEvent.media = undefined;
                existingEvent.tags = undefined;

                util.merge(existingEvent, calculateEventProperties(rawEvent));

                this.updated.push(existingEvent);
                this.items.push(existingEvent);

                // check when to stop trying to finalize
                const item = unfinished.find(byEvent, { event: existingEvent });
                if (item) {
                    if (item.tries >= eventRefreshTriesAllowed) {
                        removeFromUnfinished(existingEvent);
                    }
                }
            }

        } catch (e) {
            log({
                code: 'processEvent',
                error: e,
                traceId: this?.traceId ?? e?.traceId
            });
        }
    }

    /**
     * @description set related events (rels) for all the events that don't have them yet.
     */
    function setRels() {
        allEvents.forEach(event => {

            // handle rels for call events
            if (event?.tags?.call && !event?.rels?.call) {
                const callEvent = lookup[event.tags.call];
                if (callEvent) {
                    event.rels ??= {};
                    event.rels.call = callEvent;
                }
            }

        });
    }

    // Take the result from an events API call and creates/updates events internally.
    // Add events in the right position to reduce sorting overhead later.
    function processEvents(eventsResult) {
        if (!eventsResult?.config || !eventsResult.data.items) {
            return {};
        }

        const result = {
            added: [],
            updated: [],
            removed: [],
            items: [],
            addMethod: undefined,
            now: _window.Date.now(),
            params: undefined,
            traceId: eventsResult.traceId
        };

        // return information about this API call
        result.params = eventsResult.config.params ?? {};
        result.params.action = eventsResult.config.action;
        result.params.date = eventsResult.config.date;
        // recent events are placed at the end, while older events are placed at the beginning
        result.addMethod = (result.params.action === 'fol') ? 'push' : 'unshift';
        eventsResult.data.items.forEach(processEvent, result);

        setDeviceForEvents(eventsResult?.traceId);
        setRels();

        eventBlocks.add({ params: result.params, events: result.items });

        if (result.added.length > 0 || result.updated.length > 0 || result.removed.length > 0) {
            allEvents.sort(eventSort);
            setActives();
            // note: selected should be updated in another subject.
            activeEventsSubject.next({ actives: activeEvents, all: allEvents, updated: result.updated });

            // handle singleSubjects
            result.added.concat(result.updated).forEach(event => {
                singleSubjects?.[event.id]?.next(event);
            });
            result.removed.forEach(event => {
                singleSubjects?.[event.id]?.next();
            });
        }
        return result;
    }

    /**
     * @this {Object} iteration context with devices property
     * @param {Object} event the event to consider
     */
    function setDeviceForEvent(event) {
        if (!event.device) {
            event.device = this.devices.find(util.byId, { id: event.srcId } );
            singleSubjects[event.id]?.next(event);
        }
    }

    const existingDeviceIds = {};

    function setDeviceForEvents(traceId) {
        if (!devicesSubscription) {
            // assign device property to events any time deviceService adds new devices
            devicesSubscription = deviceService.get({
                context: deviceService.contextEnum.forEvents,
                services: true,
                traceId: traceId
            }).subscribe(devices => {
                if (Array.isArray(devices)) {
                    if (!devices.map(d => d.id).every(d => existingDeviceIds[d])) {
                        devices.map(d => d.id).forEach(d => existingDeviceIds[d] = d);
                        allEvents.forEach(setDeviceForEvent, { devices: devices });
                    }
                }
            });
        } else {
            // assign device property to all new events
            deviceService.get({
                context: deviceService.contextEnum.forEvents,
                services: true,
                traceId: traceId
            }).pipe(take(1)).subscribe(devices => {
                if (Array.isArray(devices)) {
                    allEvents.forEach(setDeviceForEvent, { devices: devices });
                }
            });
        }
    }

    function getEventData(request) {
        if (request?.action === 'id') {
            return apiService.events.getEvent(request).then(
                result => {
                    const o = { items: [] };
                    o.items.push(result.data);
                    result.data = o;
                    return processEvents(result);
                },
                e => {
                    // A 404 response is normal for a deleted event,
                    // so do not log 404, but log all other errors
                    if (e.response && e.response.status !== 404) {
                        log({
                            code: 'getEventDataById',
                            descr: 'events GET failed for single event',
                            object: request,
                            error: e,
                            traceId: e?.traceId,
                            deviceId: request.srcId
                        });
                    }
                    // process a single event that has been deleted
                    return Promise.resolve(processEvents({
                        config: e.config,
                        data: {
                            items: [{
                                id: request.id,
                                status: statusEnum.deleted
                            }]
                        },
                        traceId: e?.traceId
                    }));
                }
            );
        }
        return apiService.events.list(request).then(
            result => processEvents(result),
            e => {
                log({
                    code: 'getEventData',
                    descr: 'events GET failed for list',
                    object: request,
                    error: e,
                    traceId: e?.traceId,
                    deviceId: request?.srcId
                });
                return Promise.reject(e);
            }
        );
    }

    /**
     * @returns {Observable} emits when all data is available for calculating filterState
     */
    function prerequisites(traceId) {
        return new Observable(subscriber => {
            const traceIdObject = { traceId: traceId };
            forkJoin({
                user: userService.get(traceIdObject).pipe(util.takeFirstExistingWithNoError),
                uiSettings: userService.getUiSettings(traceIdObject).pipe(util.takeFirstExistingWithNoError),
                devices: deviceService.get({
                    context: deviceService.contextEnum.forEvents,
                    services: true,
                    traceId: traceId
                }).pipe(util.takeFirstExistingWithNoError)
            }).subscribe({
                next: result => {
                    let error;
                    Object.keys(result).forEach(key => {
                        if (result[key].isError) {
                            log({
                                code: 'prereqs',
                                descr: key,
                                error: result[key].error,
                                traceId: traceId
                            });
                            error = result[key].error;
                        }
                    });
                    if (error) {
                        subscriber.error(error);
                        return;
                    }
                    subscriber.next(result);
                    subscriber.complete();
                },
                error: e => {
                    subscriber.error(e);
                }
            });
        });
    }

    function loadEventData(request) {
        if (!request) {
            return Promise.reject();
        }
        request.v = '2.0';
        request.limit ??= 50;
        return getEventData(request);
    }

    function firstActiveEventForDateValue(dateValue, srcId) {
        if (srcId) {
            return activeEvents.find(byDateValueAndsrcId, { dateValue: dateValue, scrId: srcId});
        }
        return activeEvents.find(byDateValue, { dateValue: dateValue });
    }

    function lastActiveEventForDateValue(dateValue, srcId) {
        if (srcId) {
            return activeEvents.findLast(byDateValueAndsrcId, { dateValue: dateValue, scrId: srcId});
        }
        return activeEvents.findLast(byDateValue, { dateValue: dateValue });
    }

    // ==========================
    // check for new and unfinalized events
    // ==========================

    function checkUnfinishedEvents() {
        let requests = 0;
        const simultaneousRequestLimit = 3;
        const unfinishedCheckFrequency = 10000;
        const now = _window.Date.now();

        // refresh any events that have not been checked recently,
        // but only up to the simultaneousRequestLimit
        unfinished.forEach(function(u) {
            if (u.now + unfinishedCheckFrequency <= now && requests < simultaneousRequestLimit) {
                _eventService.getSingle({
                    id: u.event.id,
                    refresh: true
                }).pipe(take(1)).subscribe();
                u.tries += 1;
                u.now = now;
                requests += 1;
            }
        });
    }

    /**
     * @description Set this to true to allow user-generated events,
     *     but will require more resources.
     * @param {String} traceId
     * @returns {Boolean}
     */
    function supportUserEvents() {
        return false;
    }

    function loadNewEvents(traceId) {
        return new Observable(subscriber => {
            lastLoadTime = _window.Date.now();
            prerequisites(traceId).subscribe({
                next: () => {
                    const requests = {};
                    // load new cam events - one call for each visible device
                    visibleDeviceIds(traceId).forEach(source => {
                        requests[source] = loadNext({
                            srcId: source,
                            traceId: traceId
                        });
                    });

                    if (supportUserEvents()) {
                        ['user'].forEach(source => {
                            requests[source] = loadNext({
                                srcType: source,
                                traceId: traceId
                            });
                        });
                    }

                    lastLoadTime = _window.Date.now();
                    checkUnfinishedEvents();

                    forkJoin(requests).subscribe({
                        complete: () => {
                            subscriber.next();
                            subscriber.complete();
                        },
                        error: e => subscriber.error(e)
                    });
                },
                error: e => subscriber.error(e)
            });
        });
    }

    function setTimer(milliseconds) {
        _window.clearTimeout(timerForNewEvents);
        timerForNewEvents = _window.setTimeout(checkForNew, milliseconds, { timerDelay: milliseconds });
    }

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @param {number | undefined} params.timerDelay - when called by _window.setTimeout,
     *     this is the delay that was used. Otherwise undefined.
     */
    function checkForNew(params) {
        // if now minus the last check for new is bigger than the minimum time, load new events
        if ((_window.Date.now() - lastLoadTime + 1) > checkForNewMinimumInterval) {
            loadNewEvents(params?.traceId).subscribe({
                next: util.noop,
                error: util.noop
            });
        }

        // set the interval for the next check
        if (unfinished.length > 0) {
            // as long as there are unfinalized events, keep checking often
            setTimer(checkForNewMinimumInterval);
        } else {
            // allow the interval to decay when there is no UI activity
            setTimer(Math.max(checkForNewMinimumInterval, 1.5 * (params?.timerDelay ?? 0)));
        }
    }

    function getTomorrowDate(dateValue) {
        const date = new _window.Date(dateValue);
        date.setDate(date.getDate() + 1);
        date.setSeconds(date.getSeconds() + 1);
        return date;
    }

    function devicePrioritySort(deviceA, deviceB) {
        // hasEvents before not hasEvents
        if (deviceA?.status.hasEvents && !deviceB?.status.hasEvents) {
            return -1;
        } else if (!deviceA?.status.hasEvents && deviceB?.status.hasEvents) {
            return 1;
        }

        // online before offline
        if (deviceA?.status.online && !deviceB?.status.online) {
            return -1;
        } else if (!deviceA?.status.online && deviceB?.status.online) {
            return 1;
        }

        // not deleted before deleted
        if (!deviceA?.status.deleted && deviceB?.status.deleted) {
            return -1;
        } else if (deviceA?.status.deleted && !deviceB?.status.deleted) {
            return 1;
        }

        return 0;
    }

    // depends on devices being loaded first so permissions can be checked
    // depends on filters being loaded from uiSettings first
    function visibleDeviceIds(traceId) {
        const filterState = eventFilter.getState();
        const devicesResult = [];

        if (filterState && Array.isArray(filterState.devices)) {
            // only devices from filter
            filterState.devices.forEach(function(deviceId) {
                if (deviceService.hasPermission(deviceId, deviceService.permissionsEnum.eventsList)) {
                    deviceService.get({ id: deviceId, traceId: traceId })
                        .pipe(take(1))
                        .subscribe(device => {
                            if (device) {
                                devicesResult.push(device);
                            }
                        });
                }
            });
        } else {
            // all devices unfiltered
            deviceService.get({ context: deviceService.contextEnum.forEvents, traceId: traceId })
                .pipe(take(1))
                .subscribe((importedDevices) => {
                    if (importedDevices) {
                        importedDevices.forEach(function(device) {
                            if (deviceService.hasPermission(device.id, deviceService.permissionsEnum.eventsList)) {
                                if (device) {
                                    devicesResult.push(device);
                                }
                            }
                        });
                    }
                });
        }
        devicesResult.sort(devicePrioritySort);
        return devicesResult.map(device => device.id);
    }

    /**
     * @description Loads next events. Must include spec.srcId or spec.srcType, but not both
     * @param {Object} params
     * @param {String} params.eventId The event to load next from
     * @param {String} params.srcId The device to load next for
     * @param {String} params.srcType The event type to load next for
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the load is complete
     */
    function loadNext(params) {
        const internalParams = util.withTraceId(params);
        if (!(internalParams.srcId || internalParams.srcType)
                || (internalParams.srcId && internalParams.srcType)) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'loadNextParams',
                    descr: 'bad parameters',
                    object: internalParams,
                    deviceId: internalParams.srcId ?? internalParams.srcType,
                    traceId: internalParams.traceId
                })
            ));
        }

        const eventRequestSpec = {
            srcId: internalParams.srcId,
            srcType: internalParams.srcType,
            action: undefined,
            sinceEvent: undefined,
            date: undefined,
            traceId: internalParams.traceId
        };

        // try to load next based on the provided event info
        if (internalParams.eventId && lookup[internalParams.eventId]) {
            eventRequestSpec.sinceEvent = internalParams.eventId;
            eventRequestSpec.date = lookup[internalParams.eventId].fullDateValue;
            eventRequestSpec.action = 'fol';
            return jobQueue.add(loadEventData, eventRequestSpec);
        }

        // try to load previous based on the latest event
        const block = eventBlocks.latestBlockForGroup(internalParams.srcId || internalParams.srcType);
        if (block) {
            let event = eventForBlock(block, internalParams.srcId || internalParams.srcType, 'last');
            if (event) {
                eventRequestSpec.sinceEvent = event.id;
                eventRequestSpec.date = lookup[event.id].fullDateValue;
                eventRequestSpec.action = 'fol';
                return jobQueue.add(loadEventData, eventRequestSpec);
            }
        }

        // There are no events at all, so get latest in the most direct way with no extra params
        return jobQueue.add(loadEventData, eventRequestSpec);
    }

    function eventForBlock(block, groupId, position) {
        const result = allEvents.filter(event => {
            return event.srcId === groupId
                && event.fullDateValue >= block.firstDateValue
                && event.fullDateValue <= block.lastDateValue;
        }).sort(eventSort);
        if (result.length > 0) {
            if (position === 'first') {
                return result[0];
            }
            if (position === 'last') {
                return result[result.length - 1];
            }
        }
    }

    /**
     * @description Loads previous events. Must include spec.srcId or spec.srcType, but not both
     * @param {Object} params
     * @param {String} [params.eventId] The event to load previous from
     * @param {String} params.srcId The device to load previous for.
     *     Can't have both srcId and srcType, but must have one.
     * @param {String} params.srcType The event type to load previous for.
     *     Can't have both srcId and srcType, but must have one.
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the load is complete
     */
    function loadPrevious(params) {
        const internalParams = util.withTraceId(params);
        if (!(internalParams.srcId || internalParams.srcType)
                || (internalParams.srcId && internalParams.srcType)) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'loadPreviousParams',
                    descr: 'bad parameters',
                    object: params,
                    deviceId: internalParams.srcId ?? internalParams.srcType,
                    traceId: internalParams.traceId
                })
            ));
        }

        const eventRequestSpec = {
            srcId: internalParams.srcId,
            srcType: internalParams.srcType,
            action: 'pre',
            traceId: internalParams.traceId,
            beforeEvent: undefined,
            date: undefined
        };

        // try to load previous based on the provided event info
        if (internalParams.eventId && lookup[internalParams.eventId]) {
            eventRequestSpec.beforeEvent = internalParams.eventId;
            eventRequestSpec.date = lookup[internalParams.eventId].fullDateValue;
            return jobQueue.add(loadEventData, eventRequestSpec);
        }

        // try to load previous based on the earliest event
        const block = eventBlocks.earliestBlockForGroup(internalParams.srcId || internalParams.srcType);
        if (block) {
            let event = eventForBlock(block, internalParams.srcId || internalParams.srcType, 'first');
            if (event) {
                eventRequestSpec.beforeEvent = event.id;
                eventRequestSpec.date = lookup[event.id].fullDateValue;
                return jobQueue.add(loadEventData, eventRequestSpec);
            }
        }

        return new Observable(subscriber => subscriber.error(
            log({
                code: 'loadPrevious',
                descr: 'loadPrevious bad parameters or no block was found',
                object: params,
                traceId: eventRequestSpec.traceId,
                deviceId: eventRequestSpec.srcId ?? eventRequestSpec.srcType
            })
        ));
    }

    function loadDttm(spec, action) {
        if ((action !== 'startDttm' && action !== 'endDttm') || !spec?.dateValue) {
            return Promise.reject('loadDttm bad parameters');
        }
        const eventRequestSpec = {
            srcId: spec.srcId,
            srcType: spec.srcType,
            action: action,
            traceId: spec.traceId
        };
        if (action === 'endDttm') {
            eventRequestSpec.endDttm = dateValueToRoundedIsoString(spec.dateValue + 1000);
        } else {
            eventRequestSpec.startDttm = dateValueToRoundedIsoString(spec.dateValue);
        }
        return jobQueue.add(loadEventData, eventRequestSpec);
    }

    function loadMoreToFillInDate(srcId, srcType, block, event, result, traceId) {
        const group = srcId || srcType;
        if (!eventBlocks.blockForContiguousDateRange(group, event.dateValue, getTomorrowDate(event.dateValue))) {
            if (!eventBlocks.hasEarliestEvent(group)) {
                result.push(loadPrevious({
                    eventId: block.firstId,
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            }
            if (!eventBlocks.hasLatestEvent(group)) {
                result.push(loadNext({
                    eventId: block.lastId,
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            }
        }
    }

    /* eslint-disable complexity */
    function loadMoreForSourceWithBlocks(source, spec, result) {
        const firstBlock = eventBlocks.blockForEvent(source, spec.first);
        const centerBlock = eventBlocks.blockForEvent(source, spec.center);
        const lastBlock = eventBlocks.blockForEvent(source, spec.last);
        const calls = result.length;
        const traceId = spec?.traceId;

        let srcId;
        let srcType;
        if (source === 'user') {
            srcType = source;
        } else {
            srcId = source;
        }

        // load for visible range
        if (!lastBlock && !centerBlock && !firstBlock) {
            // there are blocks, but none in the visible range
            result.push(loadDttm({
                srcId: srcId,
                srcType: srcType,
                dateValue: spec.center.dateValue,
                traceId: traceId
            }, 'startDttm'));
            result.push(loadDttm({
                srcId: srcId,
                srcType: srcType,
                dateValue: spec.center.dateValue,
                traceId: traceId
            }, 'endDttm'));
        } else if (centerBlock) {
            if (!eventBlocks.equals(centerBlock, lastBlock)) {
                result.push(loadNext({
                    eventId: centerBlock.lastId,
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            } else if (!eventBlocks.equals(centerBlock, firstBlock)) {
                result.push(loadPrevious({
                    eventId: centerBlock.firstId,
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            }
        } else {
            if (lastBlock) {
                result.push(loadPrevious({
                    eventId: lastBlock.firstId,
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            } else if (firstBlock) {
                result.push(loadNext({
                    eventId: firstBlock.firstId,
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            }
        }

        // if no loads are needed for visible range, load events to complete the visible dates
        if (calls === result.length && centerBlock && spec.center) {
            loadMoreToFillInDate(srcId, srcType, centerBlock, spec.center, result, traceId);
        }
        if (calls === result.length && firstBlock && spec.first && !eventBlocks.equals(centerBlock, firstBlock)) {
            loadMoreToFillInDate(srcId, srcType, firstBlock, spec.first, result, traceId);
        }
        if (calls === result.length && lastBlock && spec.last && !eventBlocks.equals(centerBlock, lastBlock)) {
            loadMoreToFillInDate(srcId, srcType, lastBlock, spec.last, result, traceId);
        }

        // if there were still no loads made, load more events in the past
        if (spec.firstIndex !== undefined && jobQueue.length(loadEventData) === 0 && spec.firstIndex < 10 && calls === result.length) {
            if (!eventBlocks.hasEarliestEvent(source)) {
                result.push(loadPrevious({
                    srcId: srcId,
                    srcType: srcType,
                    traceId: traceId
                }));
            }
        }
    }
    /* eslint-enable complexity */

    function loadMoreForSource(source) {
        const spec = this.spec;
        const result = this.result;

        let srcId;
        let srcType;
        if (source === 'user') {
            srcType = source;
        } else {
            srcId = source;
        }

        if (eventBlocks.blockCount(source) > 0) {
            if (spec?.center) {
                loadMoreForSourceWithBlocks(source, spec, result);
            } else {
                // No events are currently visible, but a block exists, so load more of that block
                const block = eventBlocks.latestBlockForGroup(source);
                if (!eventBlocks.hasEarliestEvent(source)) {
                    result.push(loadPrevious({
                        eventId: block.firstId,
                        srcId: srcId,
                        srcType: srcType,
                        traceId: spec?.traceId
                    }));
                }
                if (!eventBlocks.hasLatestEvent(source)) {
                    result.push(loadNext({
                        eventId: block.lastId,
                        srcId: srcId,
                        srcType: srcType,
                        traceId: spec?.traceId
                    }));
                }
            }
        } else {
            // no blocks exist
            if (!eventBlocks.hasEarliestEvent(source)) {
                // did not try to get events for this device yet, so fill the visible range
                if (spec?.center) {
                    // an event is visible
                    result.push(loadDttm({
                        srcId: srcId,
                        srcType: srcType,
                        dateValue: spec.center.dateValue,
                        traceId: spec.traceId
                    }, 'startDttm'));
                    result.push(loadDttm({
                        srcId: srcId,
                        srcType: srcType,
                        dateValue: spec.center.dateValue,
                        traceId: spec.traceId
                    }, 'endDttm'));
                } else {
                    // no events are visible
                    result.push(loadNext({
                        srcId: srcId,
                        srcType: srcType,
                        traceId: spec.traceId
                    }));
                }
            }
        }
    }

    function setEventTypeForEventIdList(event) {
        if (this.list.indexOf(event.id) > -1) {
            event.type = this.value;
        }
    }

    function setEventTypeForSrcIdList(event) {
        // date criteria
        if (event.fullDateValue > this.startTime && event.date < this.endTime) {
            // favorites criteria
            if ((!this.favorites && !event.favorite) || this.favorites) {
                // srcType criteria
                if (event.srcType === this.srcType) {
                    // mark events for removal
                    if (this.list.indexOf(event.srcId) > -1) {
                        event.type = this.value;
                    }
                }
            }
        }
    }

    function doRemove(spec) {
        return apiService.events.removeById({
            eventId: spec.id,
            traceId: spec.traceId
        }).then(
            removeResult => {
                log({
                    type: 'info',
                    code: 'removeSuccess',
                    object: { eventId: removeResult?.config?.eventId },
                    traceId: spec.traceId
                });
                return removeResult;
            },
            e => {
                log({
                    code: 'removeFail',
                    object: { eventId: spec?.eventId },
                    traceId: e?.traceId,
                    error: e
                });
            }
        );
    }

    // This can take a long time for the server to process
    function doBulkRemove(spec) {
        let cleanSpec = util.withTraceId(spec);
        delete cleanSpec.name;
        return apiService.events.remove(cleanSpec).then(
            removeResult => {
                log({
                    type: 'info',
                    code: 'bulkRemoveSuccess',
                    object: removeResult?.config?.removeParams,
                    traceId: cleanSpec.traceId
                });
                return removeResult;
            },
            e => {
                log({
                    code: 'bulkRemoveFail',
                    object: { eventId: spec?.eventId },
                    traceId: e?.traceId,
                    error: e
                });
            }
        );
    }

    // gets called every time something changes with filters
    function filterInfoChange() {
        setActives();
        checkForNew();
        activeEventsSubject.next({ actives: activeEvents, all: allEvents });
    }

    function dateValueToRoundedIsoString(dateValue) {
        return new _window.Date(Math.round(dateValue / 1000) * 1000)
            .toISOString()
            .replace('.000', '');
    }

    /**
     * @description Checks if a time range needs events loaded for a single device or eventType,
     *     and then loads the data if necessary.
     * @param {String} group A deviceId or a srcType, such as 'user'.
     *     This is also equivalent to an eventBlock group.
     * @this Object An object containing properties spec, result, startTime, currentTime, endTime
     */
    function loadTimeRangeForSource(group) {
        const spec = this.spec;
        const result = this.result;
        const traceId = spec.traceId;
        const firstBlock = eventBlocks.blockForTime(group, spec.startTime);
        const currentBlock = eventBlocks.blockForTime(group, spec.currentTime);
        const lastBlock = eventBlocks.blockForTime(group, spec.endTime);

        let srcId;
        let srcType;
        if (group === 'user') {
            srcType = group;
        } else {
            srcId = group;
        }

        if (currentBlock) {
            if (!firstBlock || !eventBlocks.equals(currentBlock, firstBlock)) {
                // fill the gap in events from the current time into the past
                result.push(loadDttm({
                    srcId: srcId,
                    srcType: srcType,
                    dateValue: currentBlock.firstDateValue,
                    traceId: traceId
                }, 'endDttm'));
            }
            if (!lastBlock || !eventBlocks.equals(currentBlock, lastBlock)) {
                // fill the gap in events from the current time into the future
                result.push(loadDttm({
                    srcId: srcId,
                    srcType: srcType,
                    dateValue: currentBlock.lastDateValue,
                    traceId: traceId
                }, 'startDttm'));
            }
        } else {
            // if the requested time is close to the real current time, just get latest events,
            // else get events before and after the requested time
            if (Math.abs(Date.now() - spec.currentTime) < 5000) {
                if (!eventBlocks.hasLatestEvent(group)) {
                    result.push(loadNext({
                        srcId: srcId,
                        srcType: srcType,
                        traceId: traceId
                    }));
                }
            } else {
                // load events from the currentTime
                if (!eventBlocks.hasEarliestEvent(group)) {
                    result.push(loadDttm({
                        srcId: srcId,
                        srcType: srcType,
                        dateValue: spec.currentTime,
                        traceId: traceId
                    }, 'endDttm'));
                }
                if (!eventBlocks.hasLatestEvent(group)) {
                    result.push(loadDttm({
                        srcId: srcId,
                        srcType: srcType,
                        dateValue: spec.currentTime,
                        traceId: traceId
                    }, 'startDttm'));
                }
            }
        }
    }

    /**
     * @description emits an image with a valid height
     * @param {Object} params
     * @param {Event} params.event
     * @param {String} [params.traceId]
     * @returns {Observable<HTMLImageElement>}
     */
    function getThumbnailOneTry(params) {
        return new Observable(subscriber => {
            _eventService.thumbUri(params).subscribe({
                next(result) {
                    const xhr = new _window.XMLHttpRequest();
                    xhr.responseType = 'blob'; //so you can access the response like a normal URL
                    xhr.onreadystatechange = function () {
                        if (xhr.readyState === _window.XMLHttpRequest.DONE && xhr.status === 200) {
                            let thumb = new _window.Image();
                            thumb.eventId = params?.event?.id;
                            thumb.addEventListener('load', function() {
                                if ((this?.height ?? 0) > 0) {
                                    subscriber.next(this);
                                    subscriber.complete();
                                    return;
                                }
                                subscriber.error('thumbnail height was 0');
                            });
                            thumb.addEventListener('error', function(e) {
                                subscriber.error(e);
                            });
                            thumb.src = URL.createObjectURL(xhr.response);
                        }
                    };
                    xhr.open('GET', result, true);
                    if (environmentService.oauthToken) {
                        xhr.setRequestHeader('Authorization', 'Bearer ' + environmentService.oauthToken);
                    } else if (environmentService.sessionId) {
                        xhr.setRequestHeader('Authorization', 'SeedonkSession ' + environmentService.sessionId);
                    }
                    xhr.send();
                },
                error(e) {
                    subscriber.error(e);
                }
            });
        });
    }

    // ****************************
    // public members
    // ****************************

    /**
     * @property {module:EventService.EventTypeEnum} eventTypeEnum Type of an event.
     *     Try to avoid using event.type if you can perform business rules another way.
     * @name eventTypeEnum
     */
    _eventService.eventTypeEnum = eventTypeEnum;

    /**
     * @property {module:EventService.EventAttributes} eventAttributes attributes that an
     *     event may have - used for filtering and displaying icons and text
     * @name eventAttributes
     */
    _eventService.eventAttributes = eventAttributes;

    /**
     * @property {module:EventService.StatusEnum} statusEnum Status of the event
     * @name statusEnum
     */
    _eventService.statusEnum = statusEnum;

    /**
     * @property {module:EventService.FeedbackCodeEnum} feedbackCodeEnum Code for detection feedback
     * @name feedbackCodeEnum
     */
    _eventService.feedbackCodeEnum = feedbackCodeEnum;

    /**
     * @property {module:EventService.FeedbackCategoryEnum} feedbackCategoryEnum Detection feedback category
     * @name feedbackCategoryEnum
     */
    _eventService.feedbackCategoryEnum = feedbackCategoryEnum;

    /**
     * @description Loads the first events for a given day.
     *     Not used in the Tend web app as of Jan 2023.
     * @param {Object} params
     * @param {Number} params.dateValue - Unix timestamp for the first second of a day
     * @param {String} [params.traceId]
     * @returns {Observable} - emits when the requested events are loaded
     * @name loadFirstActiveEventsForDateValue
     * @method
     */
    _eventService.loadFirstActiveEventsForDateValue = function(params) {
        return new Observable(subscriber => {
            if (!params.dateValue) {
                subscriber.error(
                    log({
                        code: 'firstForDateValueBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            prerequisites(internalParams.traceId).subscribe({
                next: () => {
                    const requests = [];
                    visibleDeviceIds(internalParams.traceId).forEach(srcId => {
                        let date;
                        let block;
                        const event = firstActiveEventForDateValue(internalParams.dateValue, srcId);
                        if (event) {
                            // there is at least one event for this date

                            // if all events are loaded for this date (full 24 hours), do nothing
                            date = getTomorrowDate(internalParams.dateValue);
                            block = eventBlocks.blockForContiguousDateRange(
                                srcId,
                                internalParams.dateValue,
                                date.getTime()
                            );
                            if (block) {
                                return;
                            }

                            // else, load more events for this date
                            block = eventBlocks.blockForEvent(
                                srcId,
                                event
                            );
                            if (block) {
                                date = new _window.Date(block.lastDateValue - 1000);
                            } else {
                                date = new _window.Date(internalParams.dateValue);
                            }
                        } else {
                            // there are no events for this date
                            date = new _window.Date(internalParams.dateValue);
                        }
                        requests.push(loadDttm({
                            srcId: srcId,
                            dateValue: date.getTime(),
                            traceId: internalParams.traceId
                        }, 'startDttm'));
                    });

                    const result = {};
                    requests.forEach((item, index) => { result[index] = item; });
                    forkJoin(result).subscribe({
                        complete: () => {
                            checkForNew(internalParams.traceId);
                            subscriber.next(firstActiveEventForDateValue(internalParams.dateValue));
                            subscriber.complete();
                        },
                        error: e => subscriber.error(e)
                    });
                },
                error: e => subscriber.error(e)
            });
        });
    };

    /**
     * @description Loads the last events for a given day.
     * @param {Object} params
     * @param {Number} params.dateValue - Unix timestamp for the first second of a day
     * @param {String} [params.traceId]
     * @returns {Observable} - emits when the requested events are loaded
     * @name loadLastActiveEventsForDateValue
     * @method
     */
    _eventService.loadLastActiveEventsForDateValue = function(params) {
        return new Observable(subscriber => {
            if (!params.dateValue) {
                subscriber.error(
                    log({
                        code: 'lastForDateValueBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            prerequisites(internalParams.traceId).subscribe({
                next: () => {
                    const requests = [];
                    visibleDeviceIds(internalParams.traceId).forEach(srcId => {
                        let date;
                        let block;
                        const event = lastActiveEventForDateValue(internalParams.dateValue, srcId);
                        if (event) {
                            // there is at least one event for this date

                            // if all events are loaded for this date (full 24 hours), do nothing
                            date = getTomorrowDate(internalParams.dateValue);
                            block = eventBlocks.blockForContiguousDateRange(
                                srcId,
                                internalParams.dateValue,
                                date.getTime()
                            );
                            if (block) {
                                return;
                            }

                            // else, load more events for this date
                            block = eventBlocks.blockForEvent(
                                srcId,
                                event
                            );
                            if (block) {
                                date = new _window.Date(block.firstDateValue + 1000);
                            } else {
                                date = getTomorrowDate(internalParams.dateValue);
                            }
                        } else {
                            // there are no events for this date
                            date = getTomorrowDate(internalParams.dateValue);
                        }

                        requests.push(loadDttm({
                            srcId: srcId,
                            dateValue: date.getTime(),
                            traceId: internalParams.traceId
                        }, 'endDttm'));
                    });

                    // TODO: get events for srcType?

                    const result = {};
                    requests.forEach((item, index) => { result[index] = item; });
                    forkJoin(result).subscribe({
                        complete: () => {
                            checkForNew(internalParams);
                            subscriber.next(lastActiveEventForDateValue(internalParams.dateValue));
                            subscriber.complete();
                        },
                        error: e => subscriber.error(e)
                    });

                },
                error: e => subscriber.error(e)
            });
        });
    };

    /**
     * @description Returns a thumbnail image, potentially making many attempts over time
     *     to wait for a valid image
     * @param {Object} params
     * @param {Event} params.event The event to get the thumbnail for
     * @param {Number} [params.delayMs=5000] How long to wait in milliseconds between each try
     * @param {Number} [params.retries=4] How many times to retry getting the thumbnail before giving up
     * @param {String} [params.traceId]
     * @returns {Observable<HTMLImageElement>}
     */
    _eventService.getThumbnail = function(params) {
        if (!params?.event?.media?.thumbnail || !params?.event?.id) {
            return new Observable(subscriber => {
                subscriber.error('no thumbnail metadata or no event id');
            });
        }
        return new Observable(subscriber => {
            getThumbnailOneTry(params).pipe(
                retry({
                    delay: params.delayMs ?? 5000,
                    count: params.retries ?? 4
                })
            ).subscribe({
                next: result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                error: e => {
                    log({
                        code: 'loadThumbnail',
                        error: e,
                        object: {
                            event: {
                                id: params?.event?.id,
                                media: params?.event?.media,
                                type: params?.event?.type
                            },
                            delayMs: params?.delayMs,
                            retries: params?.retries
                        },
                        traceId: params.traceId
                    });
                    subscriber.error(e);
                }
            });
        });
    };

    /**
     * @description Watch for changes on a single event and load the event if not already loaded.
     *     Clients need to complete or unsubscribe from the Observable.
     * @param {Object} params A simple object to carry parameters
     * @param {String} params.id The id of the single event to get
     * @param {String} params.refresh true to force the event to reload
     * @param {String} [params.traceId]
     * @returns {Observable<Event>} An Observable that emits an Event whenever the event updates.
     * @name getSingle
     * @method
     */
    _eventService.getSingle = function(params) {
        const internalParams = util.withTraceId(params);
        if (!internalParams.id) {
            return new Observable(subscriber => subscriber.error(
                log({
                    code: 'getSingleBadParams',
                    object: params,
                    traceId: params?.traceId
                })
            ));
        }

        singleSubjects[internalParams.id] ??= new BehaviorSubject(lookup[internalParams.id]);
        // get data if not existing or refresh is requested
        if (!singleSubjects[internalParams.id].value || internalParams.refresh) {
            jobQueue.addToFront(
                loadEventData,
                {
                    action: 'id',
                    id: internalParams.id,
                    traceId: internalParams.traceId
                }
            ).subscribe({
                next: () => singleSubjects?.[internalParams.id]?.next(lookup[internalParams.id]),
                error: () => singleSubjects?.[internalParams.id]?.error('Failed to get event ' + internalParams.id)
            });
        }

        return singleSubjects[internalParams.id].asObservable();
    };

    /**
     * @description Listens for changes to loaded events.
     *     Clients Must call setFilter() before or after get() so that events are loaded.
     * @returns {Observable} Emits when changes are made to events. The return value has the form:
     *     { actives: [], all: [] } where 'actives' are filtered events and 'all' include unfiltered events.
     * @name get
     * @method
     */
    _eventService.get = function() {
        return activeEventsSubject.asObservable();
    };

    /**
     * @description Refreshes data for all loaded events,
     *     starting with events near the given time.
     *     Android needs this since Android can't request
     *     refreshing a single event like iOS can.
     *     This method is only a compromise to integrate with Android apps.
     * @param {Object} params
     * @param {Number} params.time The Unix timestamp of a date to refresh nearby events first.
     *     If falsey, then Date.now() will be used
     * @param {Object} params.traceId
     */
    _eventService.refreshAll = function(params) {
        return new Observable(subscriber => {

            allEvents.forEach(event => event.stale = true);

            const time = params?.time ?? Date.now();
            prerequisites(params?.traceId).subscribe({
                next: () => {
                    const result = {};
                    visibleDeviceIds(params?.traceId).forEach(srcId => {
                        result[srcId + 'end'] = loadDttm(
                            {
                                srcId: srcId,
                                srcType: 'cam',
                                dateValue: time,
                                traceId: params?.traceId
                            },
                            'endDttm'
                        );
                        result[srcId + 'start'] = loadDttm(
                            {
                                srcId: srcId,
                                srcType: 'cam',
                                dateValue: time,
                                traceId: params?.traceId
                            },
                            'startDttm'
                        );
                    });

                    forkJoin(result).subscribe({
                        complete: () => {
                            checkForNew(params);
                            subscriber.next();
                            subscriber.complete();
                        },
                        error: e => {
                            subscriber.error(e);
                        }
                    });
                },
                error: e => {
                    subscriber.error(e);
                }
            });
        });
    };

    /**
     * @description loads more events considering the state of
     *     an event list UI
     * @param {Object} params
     * @param {Number} params.firstIndex Index of the first visible event
     * @param {Event} params.first first visible event
     * @param {Event} params.center center visible event
     * @param {Event} params.last last visible event
     * @param {String} [params.traceId]
     * @returns {Observable} emits the number of API calls made when all the need API calls are done
     * @name loadMore
     * @method
     */
    _eventService.loadMore = function(params) {
        return new Observable(subscriber => {
            if (loadMoreInProcess) {
                _window.setTimeout(
                    function() {
                        subscriber.error('busy');
                    },
                    500
                );
                return;
            }
            loadMoreInProcess = true;
            const internalParams = util.withTraceId(params);
            prerequisites(internalParams.traceId).subscribe({
                next: () => {
                    const apiCalls = [];
                    visibleDeviceIds(internalParams.traceId).forEach(loadMoreForSource, { spec: internalParams, result: apiCalls });
                    if (supportUserEvents()) {
                        ['user'].forEach(loadMoreForSource, { spec: internalParams, result: apiCalls });
                    }
                    const result = {};
                    apiCalls.forEach((item, index) => { result[index] = item; });
                    forkJoin(result).subscribe({
                        complete: () => {
                            loadMoreInProcess = false;
                            if (activeEvents.length === 0) {
                                if (_eventService.hasEarliestEvent()) {
                                    checkForNew(internalParams);
                                }
                            } else {
                                checkForNew(internalParams);
                            }
                            subscriber.next({ actives: activeEvents, all: allEvents, apiCallsCount: apiCalls.length });
                            subscriber.complete();
                        },
                        error: e => {
                            loadMoreInProcess = false;
                            subscriber.error(e);
                        }
                    });
                },
                error: e => {
                    loadMoreInProcess = false;
                    subscriber.error(e);
                }
            });
        });
    };

    /**
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {Boolean} True if the earliest events are loaded for every visible device
     * @name hasEarliestEvent
     * @method
     */
    _eventService.hasEarliestEvent = function(params) {
        const ids = visibleDeviceIds(params?.traceId);
        return ids.every(srcId => eventBlocks.hasEarliestEvent(srcId)) && deviceService.gotAllDevices();
    };

    /**
     * @description loads more event data for a given time range,
     *     for use by time-centric controls such as timelines.
     *     This function is designed to be called frequently by clients,
     *     and will do nothing if already in progress.
     * @param {Object} params
     * @param {Number} params.startTime start time in Unix epoch milliseonds
     * @param {Number} params.endTime end time in Unix epoch milliseonds
     * @param {Number} params.currentTime the current time cursor in Unix epoch milliseonds
     * @param {String} [params.traceId]
     * @returns {Observable} emits when all the need API calls are done
     * @name loadForTimeRange
     * @method
     */
    _eventService.loadForTimeRange = function(params) {
        return new Observable(subscriber => {
            const internalParams = util.withTraceId(params);
            if (loadForTimeRangeInProcess) {
                subscriber.next('busy');
                subscriber.complete();
                return;
            }
            loadForTimeRangeInProcess = true;
            prerequisites(internalParams.traceId).subscribe({
                next: () => {
                    const apiCalls = [];
                    const now = _window.Date.now();
                    let spec = {
                        startTime: internalParams.startTime || (now - 1000 * 60 * 60), // if not provided, use now minus one hour
                        endTime: internalParams.endTime || now, // if not provided, use now
                        currentTime: internalParams.currentTime || now, // if not provided, use now
                        traceId: internalParams.traceId
                    };
                    visibleDeviceIds(internalParams.traceId).forEach(loadTimeRangeForSource, { spec: spec, result: apiCalls });
                    if (supportUserEvents()) {
                        ['user'].forEach(loadTimeRangeForSource, { spec: spec, result: apiCalls });
                    }

                    const result = {};
                    apiCalls.forEach((item, index) => { result[index] = item; });
                    forkJoin(result).subscribe({
                        complete: () => {
                            loadForTimeRangeInProcess = false;
                            subscriber.next({
                                actives: activeEvents,
                                all: allEvents,
                                apiCallsCount: apiCalls.length
                            });
                            subscriber.complete();
                        },
                        error: e => {
                            loadForTimeRangeInProcess = false;
                            subscriber.error(e);
                        }

                    });
                },
                error: e => {
                    loadForTimeRangeInProcess = false;
                    subscriber.error(e);
                }
            });
        });
    };

    /**
     * @typedef FilterInfo
     * @property {Array<Event>} actives - filtered events accrording to current filter coniditions
     * @property {Array<Event>} all - all events loaded in memory
     */

    /**
     * @returns {Observable<FilterInfo>} emits any time the internal filter state changes as a result of
     *     updating filterContext or changes to the filter settings.
     * @name getFilter
     * @method
     */
    _eventService.getFilter = function() {
        return eventFilter.get();
    };

    /**
     * @param {object|undefined} filter The user's new filter settings to be applied.
     *  If undefined, the filter state will not be updated.
     * @param {Object} filterContext specification object for the type of filter to get
     * @param {String} filterContext.deviceId set deviceId if the filter is for viewing a single device
     * @param {Boolean} filterContext.highlight true if the view is for highlight events only
     * @param {String} filterContext.ownerAlias limit events to only those from devices owned by this user
     * @param {Object} params
     * @param {String} [params.traceId]
     * @returns {undefined} No return value. Subscribe using getFilter() to be notified of changes.
     * @name setFilter
     * @method
     */
    _eventService.setFilter = function(filterContext, filter, params) {
        const internalParams = util.withTraceId(params);
        prerequisites(internalParams.traceId).subscribe({
            next: () => {
                eventFilter.set(filterContext, filter, internalParams);
            },
            error: util.noop
        });
    };

    /**
     * @description updates an existing event.
     *     Clients should observe eventService.getSingle to get new values,
     *     but can also use the emitted event
     * @param {Object} params
     * @param {Event} params.event The event being modified.
     *     Only necessary to include properties that will be updated.
     *     (Include only the delta.)
     * @param {String} params.event.id required to identify the event to update
     * @param {String} [params.traceId]
     * @returns {Observable<Event>} emits the updated event after being
     *     saved on the server and refreshed on the client
     * @name update
     * @method
     */
    _eventService.update = function(params) {
        return new Observable(subscriber => {
            if (!params?.event?.id) {
                subscriber.error(
                    log({
                        code: 'updateBadParams',
                        object: params,
                        traceId: params?.traceId,
                        deviceId: params?.event?.srcId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            const eventId = internalParams.event.id;
            delete internalParams.event.id;
            apiService.events.put({
                eventId: eventId,
                data: internalParams.event,
                traceId: internalParams.traceId
            }).then(
                () => {
                    jobQueue.addToFront(
                        loadEventData,
                        { action: 'id', id: eventId, traceId: internalParams.traceId }
                    ).subscribe({
                        next: () => subscriber.next(lookup[eventId]),
                        error: e => subscriber.error(
                            log({
                                code: 'loadAfterUpdateFail',
                                error: e,
                                traceId: internalParams.traceId,
                                deviceId: internalParams.event.srcId
                            })
                        )
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'updateFail',
                        error: e,
                        traceId: internalParams.traceId,
                        deviceId: internalParams.event.srcId
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {Event} params.event
     * @param {Number} params.num video fragment index
     * @param {Number} params.length total number of video fragments in the event
     * @param {String} [params.traceId]
     * @returns {Observable<ArrayBuffer>}
     * @name getVideoArrayBuffer
     * @method
     */
    _eventService.getVideoArrayBuffer = function(params) {
        return new Observable(subscriber => {
            apiService.events.getVideoArrayBuffer(params).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getVideoArrayBufferFail',
                        object: {
                            event: {
                                id: params?.event?.id,
                                media: params?.event?.id,
                                type: params?.event?.type
                            },
                            num: params?.num,
                            length: params?.length
                        },
                        traceId: e?.traceId,
                        error: e
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {Event} params.event
     * @param {String} [params.traceId]
     * @returns {Observable<String>} Emits Url for video
     * @name getVideoArrayBuffer
     * @method
     */
    _eventService.getVideoUri = function(params) {
        return new Observable(subscriber => {
            apiService.events.getVideoUri(params).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getVideoUriFail',
                        object: {
                            event: {
                                id: params?.event?.id,
                                media: params?.event?.media,
                                type: params?.event?.type
                            }
                        },
                        traceId: e?.traceId,
                        error: e
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {Event} params.event
     * @param {String} params.shareCode
     * @param {String} [params.traceId]
     * @returns {Observable<String>} Uri for an image frame
     * @name getDownloadUri
     * @method
     */
    _eventService.getDownloadUri = function(params) {
        return new Observable(subscriber => {
            apiService.events.getDownloadUri(params).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getDownloadUriFail',
                        object: {
                            event: {
                                id: params?.event?.id,
                                media: params?.event?.media,
                                type: params?.event?.type
                            },
                            shareCode: params?.shareCode
                        },
                        traceId: e?.traceId,
                        error: e
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {Event} params.event
     * @param {String} [params.shareCode]
     * @param {String} [params.traceId]
     * @returns {Observable<String>} Uri for an image frame
     * @name thumbUri
     * @method
     */
    _eventService.thumbUri = function(params) {
        return new Observable(subscriber => {
            apiService.events.getThumbUri(params).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => {
                    let logData = {
                        code: 'thumbUriFail',
                        object: {
                            event: {
                                id: params?.event?.id,
                                media: params?.event?.media,
                                type: params?.event?.type
                            },
                            shareCode: params?.shareCode
                        },
                        traceId: e?.traceId ?? params?.traceId,
                        error: e
                    };
                    if (!loggedThumbUriError) {
                        // only log this once per session to not produce too many logs
                        loggedThumbUriError = true;
                        log(logData);
                    }
                    subscriber.error(e);
                }
            );
        });
    };

    /**
     * @returns {Boolean}
     * @name isFilterApplied
     * @method
     */
    _eventService.isFilterApplied = function() {
        return eventFilter.isFilterApplied();
    };

    /**
     * @param {Object} params
     * @param {String} params.eventId
     * @param {String} params.shareCode
     * @param {String} [params.traceId]
     * @returns {Observable<ObjectTrackData>}
     * @name getObjectTrack
     * @method
     */
    _eventService.getObjectTrack = function(params) {
        return new Observable(subscriber => {
            apiService.events.getObjectTrack(params).then(
                result => {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getObjectTrackFail',
                        object: params,
                        traceId: e?.traceId,
                        error: e
                    })
                )
            );
        });
    };

    /**
     * @description For snapshot style events, loads the time offsets for each snapshot.
     *     Only loads frame times from the server if not already on the event,
     *     so API calls will not be made twice for the same event.
     * @param {Object} params
     * @param {Event} params.event
     * @param {String} params.shareCode
     * @param {String} [params.traceId]
     * @param {Event} event the event to get frame times for
     * @returns {Observable<Array>} emits an array of frame times
     * @name getFrameTimes
     * @method
     */
    _eventService.getFrameTimes = function(params) {
        return new Observable(subscriber => {
            if (params?.event?.media?.frameTimes?.items) {
                subscriber.next(params?.event.media.frameTimes.items);
                subscriber.complete();
                return;
            }
            if (params?.event?.id) {
                apiService.events.getFrameTimes({
                    eventId: params.event.id,
                    shareCode: params.shareCode,
                    traceId: params.traceId
                }).then(
                    result => {
                        params.event.media ??= {};
                        params.event.media.frameTimes ??= {};
                        // result.data.frameTimes will be an array, not object
                        params.event.media.frameTimes.items = result.data.frameTimes;
                        subscriber.next(params.event.media.frameTimes.items);
                        subscriber.complete();
                    },
                    e => {
                        if (Array.isArray(params.event?.media?.frameTimes?.items)) {
                            params.event.media.frameTimes.items.splice(0);
                        }
                        subscriber.error(
                            log({
                                code: 'getFrameTimesFail',
                                object: params,
                                traceId: e?.traceId,
                                error: e
                            })
                        );
                    }
                );
                return;
            }
            subscriber.error();
        });
    };

    /**
     * @description Allows clients to know if bulk removes are in progress
     * @returns {Observable<Number>} The number of bulk removes that
     *     this client started and that are still in progress.
     * @name getBulkRemoves
     * @method
     */
    _eventService.getBulkRemoves = function() {
        return bulkRemovesSubject.asObservable();
    };

    /**
     * @description Start a bulk remove operation
     * @param {Obeject} params
     * @param {Array<String>} params.srcIdList
     * @param {String} params.srcType 'cam' or 'user'
     * @param {String} params.startDttm ISO formatted date string
     * @param {String} params.endDttm ISO formatted date string
     * @param {String} [params.traceId]
     * @name bulkRemove
     * @method
     */
    _eventService.bulkRemove = function(params) {
        return new Observable(subscriber => {
            const internalParams = util.withTraceId(params);
            bulkRemovesSubject.next(bulkRemovesSubject.getValue() + 1);
            jobQueue.add(doBulkRemove, internalParams).subscribe({
                next: removeResult => {
                    const data = removeResult.config.removeParams; // use actual request params
                    // mark all events to be removed using existing property 'type'
                    if (Array.isArray(data.eventIdList)) {

                        if (Array.isArray(data.srcIdList)) {
                            log({
                                code: 'bulkRemove',
                                descr: 'cannot have both eventIdList and srcIdList',
                                object: data,
                                traceId: internalParams.traceId
                            });
                            return;
                        }

                        const eventIdIterationContext = {
                            list: data.eventIdList,
                            value: 'REMOVED'
                        };
                        allEvents.forEach(setEventTypeForEventIdList, eventIdIterationContext);

                    } else if (Array.isArray(data.srcIdList) && data.startDttm && data.endDttm) {
                        const srcIdIterationContext = {
                            list: data.srcIdList,
                            value: 'REMOVED',
                            startTime: new _window.Date(data.startDttm).getTime(),
                            endTime: new _window.Date(data.endDttm).getTime(),
                            favorites: data.favorites,
                            srcType: data.srcType
                        };
                        allEvents.forEach(setEventTypeForSrcIdList, srcIdIterationContext);
                    } else {
                        log({
                            code: 'bulkRemove',
                            descr: 'insufficient params',
                            object: data,
                            traceId: internalParams.traceId
                        });
                        return;
                    }

                    let removeIndex;
                    let i = 0;
                    while (i < allEvents.length) {
                        if (allEvents[i].type === 'REMOVED') {
                            // remove from actives
                            removeIndex = activeEvents.indexOf(allEvents[i]);
                            if (removeIndex > -1) {
                                activeEvents.splice(removeIndex, 1);
                            }
                            // remove from events and lookup
                            delete lookup[allEvents[i].id];
                            allEvents.splice(i, 1);
                            // remove from unfinished
                            removeIndex = unfinished.indexOf(allEvents[i]);
                            if (removeIndex > -1) {
                                unfinished.splice(removeIndex, 1);
                            }
                        } else {
                            i += 1;
                        }
                    }

                    deviceService.refreshHasEvents(internalParams).subscribe({
                        next: () => {
                            subscriber.next();
                            subscriber.complete();
                            activeEventsSubject.next({ actives: activeEvents, all: allEvents });
                            bulkRemovesSubject.next(bulkRemovesSubject.getValue() - 1);
                        },
                        error: (e) => {
                            subscriber.error(e);
                            activeEventsSubject.next({ actives: activeEvents, all: allEvents });
                            bulkRemovesSubject.next(bulkRemovesSubject.getValue() - 1);
                        }
                    });
                },
                error: e => {
                    subscriber.error(e);
                    bulkRemovesSubject.next(bulkRemovesSubject.getValue() - 1);
                }
            });
        });
    };

    // only ever used by mobileEventNav and eventNav to check for new events when the
    // user scrolls to the top and pulls down to load the latest.

    /**
     * @description check for and load new events, if any
     * @param {String} traceId
     * @name loadNew
     * @method
     */
    _eventService.loadNew = function(traceId) {
        // if now minus the last check for new is bigger than the minimum time, load new events
        if (!lastLoadNewDate || ((_window.Date.now() - lastLoadNewDate) > lastLoadNewInterval)) {

            // do not do if it was recently done by the auto-loader
            if ((_window.Date.now() - lastLoadTime) > lastLoadNewInterval) {
                lastLoadNewDate = _window.Date.now();
                loadNewEvents(traceId).subscribe({
                    next: util.noop,
                    error: util.noop
                });
            }
        }
    };

    /**
     * @param {Object} params
     * @param {String} params.id The ID for a single event to remove
     * @param {String} [params.traceId]
     * @returns {Observable} emits when the event has been successfully removed
     * @name remove
     * @method
     */
    _eventService.remove = function(params) {
        return new Observable(subscriber => {
            if (!params.id) {
                subscriber.error(
                    log({
                        code: 'removeBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(params);
            jobQueue.add(doRemove, { id: internalParams.id, traceId: internalParams.traceId }).subscribe({
                next: () => {
                    removeFromClient(internalParams);
                    subscriber.next();
                    subscriber.complete();
                },
                error: e => {
                    if (e.status === 400 || e.status === 404) {
                        // event was already deleted
                        removeFromClient(internalParams);
                        subscriber.next();
                        subscriber.complete();
                        log({
                            code: 'remove',
                            descr: 'already deleted',
                            object: internalParams,
                            error: e,
                            traceId: e?.traceId
                        });
                        return;
                    }
                    subscriber.error(
                        log({
                            code: 'remove',
                            descr: 'remove event failed',
                            object: internalParams,
                            error: e,
                            traceId: e?.traceId
                        })
                    );
                }
            });
        });
    };

    /**
     * @returns {FilterState} A read-only snapshot of the current filterState
     * @name getCurrentFilterState
     * @method
     */
    _eventService.getCurrentFilterState = function() {
        const info = eventFilter.getState();
        if (info) {
            return util.merge({}, info.filterState);
        }
    };

    /**
     * @param {Object} params
     * @param {String} params.group
     * @param {Number} params.startTime Unix timestamp number
     * @param {Number} params.endTime Unix timestamp number
     * @returns {Boolean}
     * @name hasAllEventsForDateRange
     * @method
     */
    _eventService.hasAllEventsForDateRange = function(params) {
        // there is a single block containing the date range
        return Boolean(eventBlocks.blockForContiguousDateRange(
            params.group,
            params.startTime,
            params.endTime
        ))
        // or tried to get data, but there are no events in the group
        || (eventBlocks.hasEarliestEvent(params.group)
                && eventBlocks.hasLatestEvent(params.group)
                && eventBlocks.blockCount(params.group) === 0);
    };

    /**
     * @param {Object} params
     * @param {String} params.eventId
     * @param {String} [params.traceId]
     * @returns {Observable<FsmData>}
     * @name getFsmData
     * @method
     */
    _eventService.getFsmData = function(params) {
        return new Observable(subscriber => {
            apiService.events.getFsmData(params).then(
                function(result) {
                    subscriber.next(result.data);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getFsmDataFail',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.eventId
     * @param {String} params.frameNum
     * @param {String} [params.traceId]
     * @returns {Observable<Object>} object containing eventId, frameNum and url
     * @name getFsmImageUri
     * @method
     */
    _eventService.getFsmImageUri = function(params) {
        return new Observable(subscriber => {
            apiService.events.getFsmImageUri(params).then(
                result => {
                    subscriber.next({
                        eventId: params.eventId,
                        frameNum: params.frameNum,
                        url: result
                    });
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getFsmImageUriFail',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {Event} params.event
     * @param {String} params.event.id
     * @param {String} params.event.srcId
     * @param {Object} params.detection
     * @param {Number} params.detection.frameNum
     * @param {Object} params.detectionSpot
     * @param {String} params.detectionSpot.spotName
     * @param {String} params.detectionSpot.correctItemId
     * @param {String} [params.traceId]
     * @returns {Observable} emits after feedback is submitted
     * @name submitVehicleFeedback
     * @method
     */
    _eventService.submitVehicleFeedback = function(params) {
        return new Observable(subscriber => {
            if (!params.event || !params.detection || !params.detectionSpot) {
                subscriber.error(params);
                return;
            }
            const dataToSubmit = {
                sourceType: 'fsmSnap',
                eventId: params.event.id,
                fsmSnapNum: params.detection.frameNum + 1,
                dbData: {
                    deviceId: params.event.srcId,
                    dbName: 'garageHog',
                    dataType: 'img',
                    tags: [{
                        name: 'spot' + params.detectionSpot.spotName,
                        value: params.detectionSpot.correctItemId,
                        tagType: 'vehicleId'
                    }]
                }
            };
            apiService.events.postAnalyticsFeedback({
                data: dataToSubmit,
                traceId: params.traceId
            }).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'submitVehicleFeedbackFail',
                        object: params,
                        error: e,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Event} event
     * @returns {Boolean} true if the event has feedback data
     * @name hasVehicleFeedbackData
     * @method
     */
    _eventService.hasVehicleFeedbackData = function(event) {
        return event?.media?.['fsmSnap.jpg'] && event?.media?.['va-GarageFSM1.json'];
    };

    /**
     * @param {Object} params
     * @param {String} params.eventId
     * @param {String} params.cropId
     * @param {String} params.shareCode
     * @param {String} [params.traceId]
     * @returns {Observable<string>} emits a URI of a face crop image
     * @name getFaceCropUri
     * @method
     */
    _eventService.getFaceCropUri = function(params) {
        return new Observable(subscriber => {
            apiService.events.getFaceCropUri(params).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @typedef Feedback
     * @description Feedback object, which may contain more properties than listed here
     * @property {String} eventId The ID of the event to submit feedback for. Required.
     * @property {String} consent a string representing the type of consent that was agreed to
     * @property {String} comments up to 255 characters of text from the user
     * @property {module:EventService.FeedbackCategoryEnum} category Category for the detection
     * @property {module:EventService.FeedbackCodeEnum} code Optional if truth is specified.
     * @property {String} truth If category is OD and no objId, then value is "<type>"" or "no-<type>"
     *     where <type> can be person, car, pet etc.
     *     If od and objId, then truth should be "<type>" or "nothing".
     *     If category is fdfr and objId, then truth should be "unknown" or id of known person.
     *     Optional if code is specified.
     * @property {String} objId indicates detected object ID associated with this feedback
     * @property {String} timeSec optional offset (in seconds) associated with this feedback
     * @property {String} traceId
     */

    /**
     * @param {Feedback} feedback
     * @returns {Observable<Event>} Emits the updated event
     * @name postFeedback
     * @method
     */
    _eventService.postFeedback = function(feedback) {
        return new Observable(subscriber => {
            if (!feedback?.eventId) {
                subscriber.error(
                    log({
                        code: 'postFeedbackNoEventId',
                        object: feedback,
                        traceId: feedback?.traceId
                    })
                );
                return;
            }
            if (!(feedback.code || feedback.truth)) {
                subscriber.error(
                    log({
                        code: 'postFeedbackCodeOrTruth',
                        object: feedback,
                        traceId: feedback?.traceId
                    })
                );
                return;
            }
            if (!Object.values(feedbackCodeEnum).some(value => feedback.code === value)) {
                subscriber.error(
                    log({
                        code: 'postFeedbackBadCode',
                        object: feedback,
                        traceId: feedback?.traceId
                    })
                );
                return;
            }
            const internalParams = util.withTraceId(feedback);
            const feedbackData = {
                consent: feedback.consent,
                comments: feedback.comments,
                category: feedback.category,
                code: feedback.code,
                truth: feedback.truth,
                objId: feedback.objId,
                timeSec: feedback.timeSec
            };
            apiService.events.postFeedback({
                eventId: internalParams.eventId,
                data: feedbackData,
                traceId: internalParams.traceId
            }).then(
                feedbackResult => {
                    jobQueue.addToFront(
                        loadEventData,
                        { action: 'id', id: internalParams.eventId, traceId: internalParams.traceId }
                    ).subscribe({
                        next: () => {
                            subscriber.next(feedbackResult.data);
                            subscriber.complete();
                        },
                        error: e => subscriber.error(
                            log({
                                code: 'postFeedbackRefreshFail',
                                object: feedback,
                                traceId: e?.traceId
                            })
                        )
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'postFeedbackFail',
                        object: feedback,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Feedback} feedback
     * @returns {Observable}
     * @name putFeedback
     * @method
     */
    _eventService.putFeedback = function(feedback) {
        return new Observable(subscriber => {
            if (!feedback?.eventId || !feedback?.feedbackId) {
                subscriber.error(
                    log({
                        code: 'putFeedbackBadParams',
                        traceId: feedback?.traceId
                    })
                );
                return;
            }
            const feedbackData = {
                consent: feedback.consent,
                comments: feedback.comments,
                category: feedback.category,
                code: feedback.code,
                truth: feedback.truth,
                objId: feedback.objId,
                timeSec: feedback.timeSec
            };
            //const traceId = feedback.traceId ?? util.randomBase64Code(15);
            const internalParams = util.withTraceId(feedback);
            apiService.events.putFeedback({
                eventId: internalParams.eventId,
                feedbackId: internalParams.feedbackId,
                data: feedbackData,
                traceId: internalParams.traceId
            }).then(
                feedbackResult => {
                    jobQueue.addToFront(
                        loadEventData,
                        { action: 'id', id: internalParams.eventId, traceId: internalParams.traceId }
                    ).subscribe({
                        next: () => {
                            subscriber.next(feedbackResult.data);
                            subscriber.complete();
                        },
                        error: e => subscriber.error(
                            log({
                                code: 'putFeedbackRefreshFail',
                                error: e,
                                object: feedback,
                                traceId: e?.traceId
                            })
                        )
                    });
                },
                e => subscriber.error(
                    log({
                        code: 'putFeedbackFail',
                        error: e,
                        object: feedback,
                        traceId: e?.traceId
                    })
                )
            );
        });
    };

    /**
     * @param {Object} params
     * @param {String} params.eventId
     * @param {Number} params.length The number of images the event has
     * @param {String} [params.shareCode] ShareCode for users not logged in
     * @param {String} [params.traceId]
     * @returns {Array<String>} An array of URIs.
     * @name getImageFrameUris
     * @method
     */
    _eventService.getImageFrameUris = function(params) {
        return new Observable(subscriber => {
            apiService.events.getImageFrameUris(params).then(
                result => {
                    subscriber.next(result);
                    subscriber.complete();
                },
                e => subscriber.error(e)
            );
        });
    };

    /**
     * @descr Creates a shareCode if the event did not have one.
     * @param {Object} params
     * @param {Event} params.event Event object
     * @param {String} params.event.id
     * @param {String} [params.traceId]
     * @returns {Observable<string>} An Observable that emits a shareCode for the given event
     * @name getShareCode
     * @method
     */
    _eventService.getShareCode = function(params) {
        return new Observable(subscriber => {
            // if the event shareCode already exists, use it immediately
            if (!params?.event?.id) {
                subscriber.error(
                    log({
                        code: 'getShareCodeBadParams',
                        object: params,
                        traceId: params?.traceId
                    })
                );
                return;
            }
            if (params.event.shareCode) {
                subscriber.next(params.event.shareCode);
                subscriber.complete();
            }
            const shareCode = util.randomBase64Code(9);
            // save the shareCode to the server
            apiService.events.put({
                eventId: params.event.id,
                data: { shareCode: shareCode },
                traceId: params.traceId
            }).then(
                () => {
                    // then use the shareCode
                    params.event.shareCode = shareCode;
                    subscriber.next(params.event.shareCode);
                    subscriber.complete();
                },
                e => subscriber.error(
                    log({
                        code: 'getShareCodeSaveFail',
                        object: params,
                        error: e,
                        traceId: params?.traceId
                    })
                )
            );
        });
    };

    // TODO: make this an observable so that previous events can be loaded first if needed
    // call loadMore with first, center and last the same event
    // or ensure existing previous event is from the same block

    /**
     * @param {Event} event
     * @name previous
     * @method
     */
    _eventService.previous = function(event) {
        const index = activeEvents.findIndex(e => e.id === event?.id);
        if (index > 0) {
            return activeEvents[index - 1];
        }
    };

    /**
     * @param {Event} event
     * @name next
     * @method
     */
    _eventService.next = function(event) {
        const index = activeEvents.findIndex(e => e.id === event?.id);
        if (index < activeEvents.length - 1) {
            return activeEvents[index + 1];
        }
    };

    /**
     * @param {Event | String} event object or eventId
     * @name eventApiUrl
     * @returns {String} Url for retrieving event data
     * @method
     */
    _eventService.eventApiUrl = function(event) {
        return apiService.events.eventApiUrl(event);
    };

    //////////////////////
    // Startup
    //////////////////////

    eventFilter.get().subscribe(filterInfoChange);

    // Any time loggedIn emits false, run clear
    apiService.loggedIn().pipe(filter(l => !l)).subscribe(() => clear());

    return Object.freeze(_eventService);
}

export default createEventService;
